import { useState, useEffect } from 'react';
import { usePagedList } from '../../hooks/usePagedList.js';
import SortableTh from '../../components/ui/SortableTh.jsx';
import Pagination from '../../components/ui/Pagination.jsx';
import { adminGetOrders, adminGetOrder, adminUpdateOrder } from '../../api/ordersApi.js';
import { fmtTime } from '../../utils/format.js';
import { useToast } from '../../context/ToastContext.jsx';

// 狀態機對齊後端 OrderStatus：pending → processing → shipping → delivered；cancelled 可隨時標記
const STATUS_OPTIONS = [
  { value: 'pending',    label: '待客服確認' },
  { value: 'processing', label: '準備出貨' },
  { value: 'shipping',   label: '運送中' },
  { value: 'delivered',  label: '已送達' },
  { value: 'cancelled',  label: '已取消' },
];

const STATUS_FILTERS = [{ value: 'all', label: '全部' }, ...STATUS_OPTIONS];

const STATUS_TO_LABEL = STATUS_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});

// API items 是物件陣列（加總數量）；mock 舊資料用 picCount
const itemCount = (o) => (Array.isArray(o.items) ? o.items.reduce((s, i) => s + (i.qty || 1), 0) : (o.picCount ?? 1));

function AdminOrdersPage() {
  const { showToast } = useToast();
  // list｜detail：點訂單編號進詳情（出貨用完整資訊），返回回列表
  const [detailId, setDetailId] = useState(null);

  return (
    <>
      <div className="admin-page-head">
        <h1>訂單管理<small>ORDERS · 客服確認 → 出貨流程</small></h1>
      </div>
      {detailId
        ? <OrderDetail id={detailId} onBack={() => setDetailId(null)} showToast={showToast} />
        : <OrderList onOpen={setDetailId} showToast={showToast} />}
    </>
  );
}

/* ── 列表 ─────────────────────────────────────────────── */
function OrderList({ onOpen, showToast }) {
  const list = usePagedList(
    p => adminGetOrders(p),
    { limit: 10, initialSort: 'createdAt,desc', initialFilters: { status: 'all' } }
  );
  const { items: orders, totalPages, page, setPage, sort, toggleSort, filters, setFilter, q, setQ, loading } = list;

  const handleStatusChange = async (id, newStatus) => {
    try {
      // 後端只收 status（label 由後端決定並自動 append timeline）；statusLabel 給 mock 顯示用
      await adminUpdateOrder(id, { status: newStatus, statusLabel: STATUS_TO_LABEL[newStatus] });
      showToast(`訂單 ${id} 已更新為「${STATUS_TO_LABEL[newStatus]}」`, 'success');
      list.refetch();
    } catch (err) {
      showToast(`更新失敗：${err.message}`, 'error');
    }
  };

  return (
    <>
      <div className="admin-card" style={{ marginBottom: 16, padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <input
            placeholder="搜尋訂單編號…"
            value={q}
            onChange={e => setQ(e.target.value)}
            style={{
              flex: 1, minWidth: 240,
              padding: '10px 14px', border: '1.5px solid var(--line)',
              borderRadius: 8, font: '600 14px Inter', outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setFilter('status', f.value)}
                style={{
                  padding: '8px 14px', borderRadius: 8,
                  border: '1.5px solid ' + (filters.status === f.value ? 'var(--navy)' : 'var(--line)'),
                  background: filters.status === f.value ? 'var(--navy)' : 'var(--white)',
                  color: filters.status === f.value ? 'var(--white)' : 'var(--ink-strong)',
                  font: '700 13px "Noto Sans TC", sans-serif',
                  cursor: 'pointer',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <SortableTh field="id" sort={sort} onSort={toggleSort}>訂單編號</SortableTh>
              <SortableTh field="createdAt" sort={sort} onSort={toggleSort}>建立時間</SortableTh>
              <th>收件人</th>
              <th>商品數</th>
              <SortableTh field="total" sort={sort} onSort={toggleSort}>金額</SortableTh>
              <SortableTh field="status" sort={sort} onSort={toggleSort} style={{ width: 110 }}>狀態</SortableTh>
              <th style={{ width: 150 }}>變更狀態</th>
              <th style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>載入中…</td></tr>
              : orders.length === 0
                ? <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>沒有符合條件的訂單</td></tr>
                : orders.map(o => (
                  <tr key={o.id}>
                    <td style={{ fontWeight: 700, color: 'var(--navy)', cursor: 'pointer' }} onClick={() => onOpen(o.id)}>{o.id}</td>
                    <td style={{ color: 'var(--ink-soft)' }}>{fmtTime(o.createdAt)}</td>
                    <td>{o.recipientName || '—'}</td>
                    <td>{itemCount(o)} 件</td>
                    <td style={{ font: '800 14px Inter', color: 'var(--navy)' }}>NT${(o.total ?? 0).toLocaleString()}</td>
                    <td><span className={`chip ${o.status}`}>{o.statusLabel}</span></td>
                    <td>
                      <select
                        value={o.status}
                        onChange={e => handleStatusChange(o.id, e.target.value)}
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          border: '1.5px solid var(--line)',
                          borderRadius: 6,
                          font: '600 13px "Noto Sans TC", sans-serif',
                          background: 'var(--white)',
                          color: 'var(--ink-strong)',
                          cursor: 'pointer',
                        }}
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td><button className="admin-btn ghost sm" onClick={() => onOpen(o.id)}>詳情</button></td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </>
  );
}

/* ── 詳情（出貨用完整資訊）──────────────────────────────── */
function OrderDetail({ id, onBack, showToast }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    adminGetOrder(id)
      .then(setOrder)
      .catch(err => showToast(`載入失敗：${err.message}`, 'error'))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [id]);

  const changeStatus = async (newStatus) => {
    setSaving(true);
    try {
      const updated = await adminUpdateOrder(id, { status: newStatus, statusLabel: STATUS_TO_LABEL[newStatus] });
      setOrder(prev => ({ ...prev, ...updated }));
      showToast(`已更新為「${STATUS_TO_LABEL[newStatus]}」`, 'success');
    } catch (err) {
      showToast(`更新失敗：${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>載入中…</div>;
  if (!order) return <div className="admin-card" style={{ padding: 40, textAlign: 'center' }}>找不到訂單 <button className="admin-btn ghost sm" onClick={onBack}>返回列表</button></div>;

  const items = Array.isArray(order.items) ? order.items : [];
  const label = (v) => <span style={{ font: '700 11px Inter', letterSpacing: '0.08em', color: 'var(--ink-soft)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>{v}</span>;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <button className="admin-btn ghost" onClick={onBack}>← 返回列表</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className={`chip ${order.status}`}>{order.statusLabel}</span>
          <select
            value={order.status}
            disabled={saving}
            onChange={e => changeStatus(e.target.value)}
            style={{ padding: '8px 12px', border: '1.5px solid var(--line)', borderRadius: 8, font: '700 13px "Noto Sans TC", sans-serif', cursor: 'pointer' }}
          >
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* 品項明細：出貨揀貨用 */}
          <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="admin-table">
              <thead>
                <tr><th>商品</th><th>規格</th><th>SKU</th><th style={{ textAlign: 'right' }}>單價</th><th style={{ textAlign: 'center' }}>數量</th><th style={{ textAlign: 'right' }}>小計</th></tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 700, color: 'var(--navy)' }}>
                      {it.name}
                      {it.itemType === 'virtual' && <span className="chip" style={{ marginLeft: 6 }}>虛擬</span>}
                    </td>
                    <td>{it.spec || '—'}</td>
                    <td style={{ font: '600 12px Inter', color: 'var(--ink-soft)' }}>{it.sku || '—'}</td>
                    <td style={{ textAlign: 'right' }}>NT${(it.unitPrice ?? 0).toLocaleString()}</td>
                    <td style={{ textAlign: 'center', fontWeight: 800 }}>{it.qty}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>NT${((it.unitPrice ?? 0) * (it.qty ?? 1)).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ textAlign: 'right', color: 'var(--ink-soft)' }}>商品小計</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>NT${(order.subtotal ?? 0).toLocaleString()}</td>
                </tr>
                <tr>
                  <td colSpan={5} style={{ textAlign: 'right', color: 'var(--ink-soft)' }}>運費{order.shippingMethodLabel ? `（${order.shippingMethodLabel}）` : ''}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{order.shipping === 0 ? '免運' : `NT$${order.shipping}`}</td>
                </tr>
                <tr>
                  <td colSpan={5} style={{ textAlign: 'right', fontWeight: 800, color: 'var(--navy)' }}>應收</td>
                  <td style={{ textAlign: 'right', font: '800 16px Inter', color: 'var(--red)' }}>NT${(order.total ?? 0).toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* 狀態歷程 */}
          <div className="admin-card" style={{ padding: '18px 22px' }}>
            {label('狀態歷程 TIMELINE')}
            {(order.timeline || []).map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--line)', font: '600 13px "Noto Sans TC", sans-serif' }}>
                <span style={{ color: 'var(--ink-soft)', minWidth: 130 }}>{t.time || fmtTime(t.createdAt)}</span>
                <b style={{ color: 'var(--navy)' }}>{t.label}</b>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* 訂單資訊 */}
          <div className="admin-card" style={{ padding: '18px 22px' }}>
            {label('訂單資訊')}
            <div style={{ font: '800 16px Inter', color: 'var(--navy)' }}>{order.id}</div>
            <div style={{ font: '600 13px "Noto Sans TC", sans-serif', color: 'var(--ink-soft)', marginTop: 4 }}>{fmtTime(order.createdAt)}</div>
          </div>

          {/* 出貨資訊：收件人 + 配送方式 */}
          <div className="admin-card" style={{ padding: '18px 22px', font: '600 14px "Noto Sans TC", sans-serif', lineHeight: 1.9 }}>
            {label('出貨資訊 SHIPPING')}
            <div><b>{order.recipientName}</b>　{order.recipientPhone}</div>
            <div>{order.recipientAddress}</div>
            <div style={{ color: 'var(--ink-soft)' }}>{order.shippingMethodLabel || order.shippingMethod || '—'}</div>
          </div>

          {/* 聯繫資訊：專人結帳要主動聯繫客人 */}
          <div className="admin-card" style={{ padding: '18px 22px', font: '600 14px "Noto Sans TC", sans-serif', lineHeight: 1.9 }}>
            {label('客服聯繫 CONTACT')}
            <div>{order.contactMethodLabel || order.contactMethod || '—'}</div>
            {order.contactInfo && <div style={{ color: 'var(--navy)' }}>{order.contactInfo}</div>}
          </div>
        </div>
      </div>
    </>
  );
}

export default AdminOrdersPage;
